import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getMyMessages, markMessagesRead, getMyNetwork } from '../api/messages';

function timeAgo(dateStr) {
  const diff = (Date.now() - new Date(dateStr)) / 1000;
  if (diff < 60) return '방금 전';
  if (diff < 3600) return `${Math.floor(diff / 60)}분 전`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}시간 전`;
  return `${Math.floor(diff / 86400)}일 전`;
}

function strengthBar(strength) {
  const max = 80;
  const pct = Math.min(strength / max, 1);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ width: 60, height: 4, background: '#eee', borderRadius: 2 }}>
        <div style={{ width: `${pct * 100}%`, height: '100%', background: '#333', borderRadius: 2 }} />
      </div>
      <span style={{ fontSize: '0.75em', color: '#999' }}>{strength}pt</span>
    </div>
  );
}

export default function DashboardPage({ defaultTab = 'messages' }) {
  const { user, isLoading, refreshUnreadCount } = useAuth();
  const [messages, setMessages] = useState([]);
  const [network, setNetwork] = useState([]);
  const [tab, setTab] = useState(defaultTab);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isLoading || !user) return;
    async function load() {
      const idToken = await user.getIdToken();
      const [msgs, conns] = await Promise.all([
        getMyMessages(idToken),
        getMyNetwork(idToken),
      ]);
      setMessages(msgs);
      setNetwork(conns);
      setLoading(false);
    }
    load();
  }, [user, isLoading]);

  const handleMessageClick = async (cardId) => {
    if (!user) return;
    const idToken = await user.getIdToken();
    markMessagesRead(cardId, idToken);
    setMessages(prev =>
      prev.map(m => m.cardId === cardId ? { ...m, read: true } : m)
    );
    refreshUnreadCount();
  };

  if (isLoading || loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <p style={{ color: '#999', fontSize: '0.9em' }}>불러오는 중...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: 12 }}>
        <p style={{ color: '#333' }}>로그인이 필요합니다</p>
        <a href="/" style={{ color: '#666', fontSize: '0.85em' }}>홈으로 →</a>
      </div>
    );
  }

  const unreadCount = messages.filter(m => !m.read).length;

  return (
    <div style={{ maxWidth: 480, margin: '0 auto', padding: '24px 16px 80px' }}>
      {/* 헤더 */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <h2 style={{ fontSize: '1.2em', fontWeight: 700, margin: 0 }}>대시보드</h2>
        <a href="/" style={{ fontSize: '0.8em', color: '#666', textDecoration: 'none' }}>← 에디터</a>
      </div>

      {/* 탭 */}
      <div style={{ display: 'flex', borderBottom: '1px solid #e5e5e5', marginBottom: 20 }}>
        {[
          { key: 'messages', label: `받은 쪽지${unreadCount > 0 ? ` (${unreadCount})` : ''}` },
          { key: 'network', label: `만난 사람들 (${network.length})` },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              flex: 1, padding: '10px 0', background: 'none', border: 'none',
              borderBottom: tab === t.key ? '2px solid #000' : '2px solid transparent',
              fontWeight: tab === t.key ? 700 : 400,
              fontSize: '0.85em', cursor: 'pointer', color: tab === t.key ? '#000' : '#999',
              transition: 'all 0.15s',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* 받은 쪽지 탭 */}
      {tab === 'messages' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {messages.length === 0 ? (
            <p style={{ color: '#999', fontSize: '0.85em', textAlign: 'center', marginTop: 40 }}>
              아직 받은 쪽지가 없어요
            </p>
          ) : messages.map(m => (
            <div
              key={m.id}
              onClick={() => handleMessageClick(m.cardId)}
              style={{
                padding: '14px 16px',
                border: '1px solid',
                borderColor: m.read ? '#e5e5e5' : '#000',
                borderRadius: 8,
                cursor: 'pointer',
                background: m.read ? '#fff' : '#fafafa',
                transition: 'border-color 0.15s',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  {m.senderCardId ? (
                    <a
                      href={`/card/${m.senderCardId}`}
                      onClick={e => e.stopPropagation()}
                      style={{ fontWeight: 600, fontSize: '0.9em', color: '#000', textDecoration: 'none' }}
                    >
                      {m.senderName || '익명'}
                    </a>
                  ) : (
                    <span style={{ fontWeight: 600, fontSize: '0.9em' }}>{m.senderName || '익명'}</span>
                  )}
                  {!m.read && (
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#000', display: 'inline-block' }} />
                  )}
                </div>
                <span style={{ fontSize: '0.75em', color: '#999' }}>{timeAgo(m.sentAt)}</span>
              </div>
              <p style={{ margin: 0, fontSize: '0.88em', color: '#333', lineHeight: 1.5 }}>{m.text}</p>
            </div>
          ))}
        </div>
      )}

      {/* 만난 사람들 탭 */}
      {tab === 'network' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {network.length === 0 ? (
            <p style={{ color: '#999', fontSize: '0.85em', textAlign: 'center', marginTop: 40 }}>
              아직 만난 사람이 없어요
            </p>
          ) : network.map(conn => (
            <div
              key={conn.uid}
              style={{
                padding: '14px 16px', border: '1px solid #e5e5e5',
                borderRadius: 8, background: '#fff',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  {conn.shortId ? (
                    <a
                      href={`/card/${conn.shortId}`}
                      style={{ fontWeight: 600, fontSize: '0.9em', color: '#000', textDecoration: 'none' }}
                    >
                      {conn.name || '명함 없는 사용자'}
                    </a>
                  ) : (
                    <span style={{ fontWeight: 600, fontSize: '0.9em' }}>{conn.name || '명함 없는 사용자'}</span>
                  )}
                  {(conn.company || conn.title) && (
                    <p style={{ margin: '2px 0 0', fontSize: '0.8em', color: '#666' }}>
                      {[conn.company, conn.title].filter(Boolean).join(' · ')}
                    </p>
                  )}
                </div>
                <span style={{ fontSize: '0.75em', color: '#999' }}>{timeAgo(conn.lastInteractionAt)}</span>
              </div>
              <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                {strengthBar(conn.strength)}
                {conn.shortId && (
                  <a
                    href={`/card/${conn.shortId}?msg=1`}
                    style={{
                      fontSize: '0.78em', color: '#666', textDecoration: 'none',
                      border: '1px solid #e5e5e5', borderRadius: 4, padding: '3px 10px',
                    }}
                  >
                    쪽지 보내기
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
